import { TimelineMax, Elastic, Power4 } from 'gsap/TweenMax';

import store from '@/store';
import ConfucioGroup from './groups/ConfucioGroup';
import CatGroup from './groups/CatGroup';
import ComputerGroup from './groups/ComputerGroup';
import GhostGroup from './groups/GhostGroup';
import HamburgerGroup from './groups/HamburgerGroup';
import PolaroidGroup from './groups/PolaroidGroup';
import RocketGroup from './groups/RocketGroup';
import UfoGroup from './groups/UfoGroup';
import BookGroup from './groups/BookGroup';
import Scene from './Scene';
import Camera from './Camera';
import Renderer from './Renderer';

import { degToRad } from '@/scripts/utils';
import { getGroup } from '@/scripts/groupUtils';


class CoreGroup {
  constructor() {
    this.clones = [];
  }

  init() {
    ConfucioGroup.model.position.set(8, -10, 8);
    ComputerGroup.model.position.set(8, -10, 8);
    PolaroidGroup.model.position.set(8, -10, 8);
    HamburgerGroup.model.position.set(8, -10, 8);
    CatGroup.model.position.set(8, -10, 8);
    GhostGroup.model.position.set(8, -10, 8);
    RocketGroup.model.position.set(8, -10, 8);
    UfoGroup.model.position.set(8, -10, 8);
    BookGroup.model.position.set(8, -10, 8);
    this.setFirst();
  }

  setFirst() { // eslint-disable-line
    const currentGroup = getGroup.byIndex(store.state.currentIndex);
    const currentModel = getGroup.byIndex(store.state.currentIndex).model;
    Scene.add(currentModel);
    currentGroup.createClones();

    currentModel.position.x = 8;
    currentModel.position.y = -10;
    currentModel.position.z = 8;
    Renderer.compile(Scene, Camera);

    const tl = new TimelineMax();

    tl.add('start')
      .to(currentModel.position, 2, {
        x: 0,
        y: 0,
        z: 0,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .to(currentModel.rotation, 1.5, {
        y: `+=${degToRad(705)}`,
        ease: Power4.easeOut,
      }, 'start');

    currentGroup.enterClones();
  }

  slideFromTo(prevIndex, nextIndex) { // eslint-disable-line
    const prevGroup = getGroup.byIndex(prevIndex);
    const prevModel = prevGroup.model;
    const nextGroup = getGroup.byIndex(nextIndex);
    const nextModel = nextGroup.model;
    const prev = {
      x: prevIndex < nextIndex ? -15 : 8,
      y: prevIndex < nextIndex ? 15 : -10,
      z: prevIndex < nextIndex ? -8 : 8,
    };

    nextModel.position.set(
      nextIndex < prevIndex ? -15 : 8,
      nextIndex < prevIndex ? 15 : -10,
      nextIndex < prevIndex ? -8 : 8,
    );

    const direction = nextIndex > prevIndex ? 'left' : 'right';
    const direction1 = nextIndex < prevIndex ? 'left' : 'right';
    const tl = new TimelineMax();

    tl.add('start')
      .add(() => {
        if (!prevGroup.clonesAreExit) prevGroup.exitClones(direction1);
      }, 'start')
      .to(prevModel.position, 2, {
        x: prev.x,
        y: prev.y,
        z: prev.z,
        ease: Power4.easeInOut,
      }, 'start')
      .to(prevModel.rotation, 2, {
        y: 0,
        x: 0,
        z: 0,
        ease: Power4.easeInOut,
      }, 'start')
      .to(prevModel.scale, 2, {
        x: 1,
        y: 1,
        z: 1,
        ease: Power4.easeInOut,
      }, 'start')
      .to(nextModel.position, 2.5, {
        x: 0,
        y: store.state.scrollIsLocked ? 0 : nextIndex === 0 || nextIndex === 8 ? 0 :4.5, // eslint-disable-line
        z: 0,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start+=1.2')
      .to(nextModel.scale, 2.5, {
        x: store.state.scrollIsLocked ? 1 : nextIndex === 0 || nextIndex === 8 ? 1 : 1.5, // eslint-disable-line
        y: store.state.scrollIsLocked ? 1 : nextIndex === 0 || nextIndex === 8 ? 1 : 1.5, // eslint-disable-line
        z: store.state.scrollIsLocked ? 1 : nextIndex === 0 || nextIndex === 8 ? 1 : 1.5, // eslint-disable-line
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start+=1.2')
      .add(() => {
        nextGroup.createClones();
        nextGroup.enterClones(direction);
      }, 'start+=1.05')
      .to(nextModel.rotation, 2, {
        y: `+=${degToRad(705)}`,
        x: store.state.scrollIsLocked ? 0 : nextIndex === 0 || nextIndex === 8 ? 0 : degToRad(10), // eslint-disable-line
        z: store.state.scrollIsLocked ? 0 :nextIndex === 0 || nextIndex === 8 ? 0 : degToRad(10), // eslint-disable-line
        ease: Power4.easeOut,
      }, 'start+=1.2');

    return tl;
  }

  enterPage(index) {
    const currentGroup = getGroup.byIndex(index);
    const currentModel = currentGroup.model;
    const tl = new TimelineMax({
      onComplete: () => {
        currentGroup.stopFlow();
      },
    });

    tl.add('start')
      .to(currentModel.position, 2.5, {
        y: 4.5,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .to(currentModel.scale, 2.5, {
        x: 1.5,
        y: 1.5,
        z: 1.5,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .to(currentModel.rotation, 2.5, {
        x: degToRad(10),
        z: degToRad(10),
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .add(() => {
        currentGroup.exitClones();
      }, 'start');

    return tl;
  }

  enterPageHold(index) {
    const currentGroup = getGroup.byIndex(index);
    const currentModel = currentGroup.model;
    const tl = new TimelineMax({
      onComplete: () => {
        if (!currentGroup.clonesAreExit) {
          currentGroup.stopFlow();
          currentGroup.exitClones();
        }
      },
    });

    tl.add('start')
      .to(currentModel.position, 2, {
        y: 4.5,
        ease: Power4.easeOut,
      }, 'start')
      .to(currentModel.scale, 2, {
        x: 1.5,
        y: 1.5,
        z: 1.5,
        ease: Power4.easeOut,
      }, 'start')
      .to(currentModel.rotation, 2, {
        x: degToRad(10),
        z: degToRad(10),
        ease: Power4.easeOut,
      }, 'start');

    return tl;
  }

  leavePageHold(index) {
    const currentGroup = getGroup.byIndex(index);
    const currentModel = currentGroup.model;
    const tl = new TimelineMax();

    tl.add('start')
      .to(currentModel.position, 1.8, {
        y: 0,
        ease: Power4.easeOut,
      }, 'start')
      .to(currentModel.scale, 1.8, {
        x: 1,
        y: 1,
        z: 1,
        ease: Power4.easeOut,
      }, 'start')
      .to(currentModel.rotation, 1.8, {
        x: 0,
        z: 0,
        ease: Power4.easeOut,
      }, 'start');

    return tl;
  }


  leavePage(index) {
    const currentGroup = getGroup.byIndex(index);
    const currentModel = currentGroup.model;
    const tl = new TimelineMax({
      onStart: () => {
        currentGroup.startFlow();
      },
    });

    tl.add('start')
      .to(currentModel.position, 1.8, {
        y: 0,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .to(currentModel.scale, 1.8, {
        x: 1,
        y: 1,
        z: 1,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .to(currentModel.rotation, 1.8, {
        x: 0,
        z: 0,
        ease: Elastic.easeOut.config(1, 0.5),
      }, 'start')
      .add(() => {
        currentGroup.enterClones();
      }, 'start');

    return tl;
  }
}

export default new CoreGroup();
